import { Menu, Transition } from '@headlessui/react';
import { Fragment, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { ArrowRightIcon } from '~components/icons';
import { MoreIcon } from '~components/icons/MoreIcon';
import useLabels from '~hooks/useLabels';
import { AssetActions } from '~modules/media-library/model/AssetActions';
import { Button } from '~modules/media-library/view/Button';
import PermissionsList from '~modules/media-library/view/PermissionsList/PermissionsList';
import { TeaserMenuItemButton } from '~modules/media-library/view/Teaser/TeaserMenuItemButton';
import { ModalActions } from '~store/actions';

type AssetMenuProps = {
  edit: boolean;
  editPermissions: boolean;
  raiseRequest: boolean;
  deleteAllowed: boolean;
  assetId: string;
  AssetActions: AssetActions;
};

export function AssetMenu({
  edit,
  editPermissions,
  assetId,
  raiseRequest,
  deleteAllowed,
  AssetActions,
}: AssetMenuProps) {
  const { btnAssetPermissions } = useLabels(['btnAssetPermissions']);

  const dispatch = useDispatch();

  const permissionsModalConfig = useMemo(() => {
    return {
      type: 'string',
      headline: `Permissions`,
      isVisible: true,
      children: <PermissionsList assetId={assetId} />,
      width: '70%',
      height: 'fit-content',
      onCloseClick: () => {},
    };
  }, [assetId]);

  return (
    <div className="ml--teaser-actions flex justify-between gap-2 flex-shrink-0">
      <Menu as="div" className="ml--teaser-menu relative inline-flex">
        {({ open }) => (
          <>
            <Menu.Button as={Button} variant="icon" active={open}>
              <MoreIcon />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 bottom-full mb-2 min-w-40 origin-bottom-right outline-none z-20">
                <div className="ml--teaser-menu--panel rounded bg-white shadow-xl flex flex-col w-full p-3">
                  {edit && (
                    <Menu.Item>
                      <TeaserMenuItemButton
                        title=""
                        className="ml-icon-arrow-right"
                        onClick={() => AssetActions.openContributionWizard(assetId, 'edit-single')}
                        focus={false}
                      >
                        <ArrowRightIcon className="w-5 h-5 mr-2" />
                        Edit
                      </TeaserMenuItemButton>
                    </Menu.Item>
                  )}
                  {editPermissions && (
                    <Menu.Item>
                      <TeaserMenuItemButton
                        title=""
                        className="ml-icon-arrow-right"
                        onClick={() => {
                          dispatch(ModalActions.show(permissionsModalConfig));
                        }}
                        focus={false}
                      >
                        <ArrowRightIcon className="w-5 h-5 mr-2" />
                        {btnAssetPermissions}
                      </TeaserMenuItemButton>
                    </Menu.Item>
                  )}
                  {raiseRequest && (
                    <Menu.Item>
                      <TeaserMenuItemButton
                        title=""
                        className="ml-icon-arrow-right"
                        onClick={() => AssetActions.onUseRaiseRequestAssets(assetId)}
                        focus={false}
                      >
                        <ArrowRightIcon className="w-5 h-5 mr-2" />
                        Raise Request
                      </TeaserMenuItemButton>
                    </Menu.Item>
                  )}
                  {edit && (
                    <Menu.Item>
                      <TeaserMenuItemButton
                        title=""
                        className="ml-icon-arrow-right"
                        onClick={() => AssetActions.openContributionWizard(assetId, 'replace')}
                        focus={false}
                      >
                        <ArrowRightIcon className="w-5 h-5 mr-2" />
                        Replace File
                      </TeaserMenuItemButton>
                    </Menu.Item>
                  )}
                  {deleteAllowed && (
                    <Menu.Item>
                      <TeaserMenuItemButton
                        title=""
                        className="ml-icon-arrow-right"
                        onClick={() => AssetActions.onConfirmDelete(assetId)}
                        focus={false}
                      >
                        <ArrowRightIcon className="w-5 h-5 mr-2" />
                        Delete
                      </TeaserMenuItemButton>
                    </Menu.Item>
                  )}
                </div>
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
}
